import deepseacreatureImage from '../Assets/Invasives/deepseacreature.png';
import blobfishImage from '../Assets/Invasives/blobfish.png';
import React, { useEffect, useRef, useState, useContext } from 'react';
import { SoundContext } from '../Context/SoundContext';
import TimerDisplay from '../Components/TimerDisplay';

import prawnImage from '../Assets/prawn.png';
import prawn8kImage from '../Assets/prawn-8k.png';
import monkfishImage from '../Assets/Invasives/monkfish.png';
import smilingsalamanderImage from '../Assets/Invasives/smilingsalamander.png';
import confetti from 'canvas-confetti';
import { useModal, MODAL_TYPES } from '../Context/ModalContext';
import UnderwaterBloodEffect from '../Components/UnderwaterBloodEffect';
import MovingSprites from '../Components/MovingSprites';
import { useBatchUpdates } from '../Utils/batchUpdates';
import { trackEvent } from '../Utils/analytics';


// Import all PNGs in Invasives folder, including specials, in sorted order for consistency
const invasivesContext = require.context('../Assets/Invasives', false, /\.png$/);
const allInvasiveImages = invasivesContext.keys().map(invasivesContext);


// Unified prawn and invasive config
const PRAWN_SIZE_RANGE = [48, 72]; // px
const PRAWN_SPEED_RANGE = [1.1, 1.9]; // px/frame (slightly faster)
const INVASIVE_SIZE_RANGE = [37, 64];
const INVASIVE_SPEED_RANGE = [2.9, 3.8]; // px/frame (swapped)

const KillSimulator = ({ setTimer, isHdPrawnMode }) => {
  // Ref to ensure celebration SFX only plays once per game
  const celebrationPlayedRef = useRef(false);
  const { openModal, modal } = useModal();
  const { playSoundFx } = useContext(SoundContext);
  const { batchUpdate } = useBatchUpdates();
  // 1 in 100 prawns is fried
  const [prawnStates, setPrawnStates] = useState(
    Array(5).fill().map(() => ({
      alive: true,
      isFried: Math.floor(Math.random() * 100) === 0
    }))
  );
  // All invasives appear once
  const invasiveImageIndexesArr = [];
  allInvasiveImages.forEach((img, i) => {
    invasiveImageIndexesArr.push(i);
  });
  // Shuffle for randomness
  function shuffle(arr) {
    let a = arr.slice();
    for (let i = a.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
  }
  const [invasiveImageIndexes] = useState(() => shuffle(invasiveImageIndexesArr));
  const [invasiveStates, setInvasiveStates] = useState(Array(invasiveImageIndexesArr.length).fill().map(() => ({ alive: true })));
  const [gameComplete, setGameComplete] = useState(false);
  const [gameLost, setGameLost] = useState(false);
  const [time, setTime] = useState(0);
  // Buffer for timer updates to parent
  const lastReportedTimeRef = useRef(0);
  const [gameStarted, setGameStarted] = useState(false);
  // intentionally unused, silences ESLint
  // eslint-disable-next-line no-unused-vars
  const [invasiveKillCount, setInvasiveKillCount] = useState(0);
  const [bloodEffect, setBloodEffect] = useState(null); // { x, y, onComplete }
  const [laserEffect, setLaserEffect] = useState(null); // { x, y, hit, id }
  const [collateralEffect, setCollateralEffect] = useState(null); // { x, y, position, id }
  const timerRef = useRef(null);

  // Timer logic
  useEffect(() => {
    if (gameStarted && !gameComplete && !gameLost) {
      timerRef.current = setInterval(() => {
        setTime(prev => prev + 10);
      }, 10);
    }
    return () => clearInterval(timerRef.current);
  }, [gameStarted, gameComplete, gameLost]);

  // Only update parent timer when local time changes, and only in an effect
  useEffect(() => {
    if (typeof setTimer === 'function' && time !== lastReportedTimeRef.current) {
      setTimer(time);
      lastReportedTimeRef.current = time;
    }
  }, [time, setTimer]);

  useEffect(() => {
    if (!gameStarted) {
      setTime(0); // reset local time
      setTimer(0); // reset parent timer
      setInvasiveKillCount(0);
    }
    // Reset celebration trigger on new game
    celebrationPlayedRef.current = false;
  }, [gameStarted, setTimer]);

  // Check for game completion when invasiveStates changes
  useEffect(() => {
    if (
      invasiveStates.every(inv => !inv.alive) &&
      !gameComplete &&
      !celebrationPlayedRef.current &&
      gameStarted
    ) {
      stopTimer();
      setGameComplete(true);
      celebrationPlayedRef.current = true;
      if (playSoundFx) playSoundFx('celebration');
      confetti({
        particleCount: 100,
        spread: 360,
        origin: { x: 0.5, y: 0.5 },
        gravity: 0.3,
        scalar: 0.8,
        drift: 0.1,
        ticks: 250,
        // Colors inspired by desktopbackground.jpg (coral, blue, yellow, orange, green, brown) plus #45EBA5 and #EB9F45
        colors: [
          '#45EBA5', // bright green
          '#EB9F45', // orange-gold
          '#F25C05', // vivid orange coral
          '#E86A92', // pink coral
          '#3A9AD9', // blue highlight
          '#A5E8D2', // aqua
        ]
      });
      // Format the time for tweet intent as in congrats modal, but with ' seconds' and ' minutes and'
      const formatTweetTime = (time) => {
        const milliseconds = Math.floor((time % 1000) / 10);
        const seconds = Math.floor((time / 1000) % 60);
        const minutes = Math.floor((time / (1000 * 60)) % 60);
        const totalSeconds = minutes * 60 + seconds + (milliseconds / 1000);
        let formattedTime;
        if (totalSeconds < 61) {
          // Format as x.xx seconds if under 61 seconds
          formattedTime = `${seconds}.${milliseconds.toString().padStart(2, '0')} seconds.`;
        } else {
          // Format as x minutes and x.xx seconds if 61 seconds or over
          formattedTime = `${minutes} minutes and ${seconds}.${milliseconds.toString().padStart(2, '0')} seconds.`;
        }
        return formattedTime;
      };
      trackEvent('game_won', { duration_ms: time });
      openModal(MODAL_TYPES.CONGRATS, {
        time: formatTime(time),
        handleTweetClick: () => {
          trackEvent('external_link', { destination: 'tweet_intent', source: 'congrats_modal' });
          const formattedTime = formatTweetTime(time);
          const tweetText = `This is a prawn website.\n\nI defended the vibe and it only took ${formattedTime}\n\n@labbylabsxyz\n\nhttps://prawnhub.xyz`;
          const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`;
          window.open(tweetUrl, '_blank');
        }
      });
    }
  }, [invasiveStates, gameComplete, gameStarted, playSoundFx, openModal, time]);

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const formatTime = (time) => {
    const milliseconds = Math.floor((time % 1000) / 10);
    const seconds = Math.floor((time / 1000) % 60);
    const minutes = Math.floor((time / (1000 * 60)) % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}:${milliseconds < 10 ? '0' : ''}${milliseconds}`;
  };

  // ...existing code...

  // Unified shooting logic for prawns and invasives
  const handlePrawnShot = (i, x, y) => {
    if (modal.isOpen || gameComplete || gameLost) return;
    if (!gameStarted) setGameStarted(true);
    // Set gameLost immediately to block all further input, even before modal opens
    setGameLost(true);
    setLaserEffect({ x, y, hit: true, id: Date.now() + Math.random() });
    setTimeout(() => setLaserEffect(null), 180);
    setPrawnStates(prev => {
      const newStates = [...prev];
      newStates[i].alive = false;
      return newStates;
    });
    // Play prawn hit SFX for kill
    playSoundFx('shrimpShotShort');
    window.bloodEffectActive = true; // Global flag for MovingSprites throttling
    setBloodEffect({
      x, y,
      onComplete: () => {
        // Use batchUpdate to prevent CLS timing collisions with MovingSprites animation loop
        batchUpdate(() => {
          setBloodEffect(null);
          window.bloodEffectActive = false; // Clear global flag
          stopTimer();
          // openModal after gameLost is set, but only if not already open
          trackEvent('game_lost', { duration_ms: time });
          openModal(MODAL_TYPES.GAME_LOST, { time: formatTime(time) });
        });
      }
    });
  };

  // Map sprite filenames to SFX keys
  const invasiveSfxMap = {
    'blobfish2.png': 'invasiveShots_0', // ph-invasive-shot-1
    'anglerfish.png': 'invasiveShots_1', // ph-invasive-shot-2
    'deepseacreature.png': 'invasiveShots_2', // ph-invasive-shot-3
    'vape.png': 'invasiveShots_3', // ph-invasive-shot-4
    'burger.png': 'invasiveShots_4', // ph-invasive-shot-5
    'creepytoy.png': 'invasiveShots_14', // ph-invasive-shot-19 (index 14)
    'monkfish.png': 'invasiveShots_7', // ph-invasive-shot-8
    'smilingsalamander.png': 'invasiveShots_8', // ph-invasive-shot-9
    'syringe.png': 'invasiveShots_3', // ph-invasive-shot-4 (same as vape)
    'wrapper.png': 'invasiveShots_10', // ph-invasive-shot-11
    'straw.png': 'invasiveShots_11', // ph-invasive-shot-12
    'energydrink.png': 'invasiveShots_11', // ph-invasive-shot-12 (corrected)
    'blobfish3.png': 'invasiveShots_12', // ph-invasive-shot-13
    'blobfish.png': 'invasiveShots_13', // ph-invasive-shot-14
    'waterbottle.png': 'invasiveShots_3', // ph-invasive-shot-4 (same as vape)
    // New invasives
    'gascan.png': 'plasticShot4', // same as toaster.png
    'mitchface.png': 'invasiveShots_12', // same as blobfish3.png
    'markface.png': 'invasiveShots_1', // same as anglerfish.png
    // Plastic shots
    'sixpackring.png': 'plasticShot1', // ph-plastic-shot-1
    'severedhand.png': 'plasticShot2', // ph-plastic-shot-2
    'plasticbag.png': 'plasticShot3', // ph-plastic-shot-3
    'toaster.png': 'plasticShot4', // ph-plastic-shot-4
  };

  // Collateral SFX suppression state (guaranteed global window)
  const collateralRef = useRef({ until: 0 });

  const handleInvasiveShot = (i, x, y, event) => {
    if (modal.isOpen || gameComplete || gameLost) return;
    // Prevent missed shot sound by stopping event propagation if event exists
    if (event && typeof event.stopPropagation === 'function') event.stopPropagation();
    if (!gameStarted) setGameStarted(true);
    setLaserEffect({ x, y, hit: true, id: Date.now() + Math.random() });
    setTimeout(() => setLaserEffect(null), 180);

    // Collateral logic: check how many invasives are hit by this shot
    if (!handleInvasiveShot.lastShotInfo) handleInvasiveShot.lastShotInfo = { pos: null, count: 0, time: 0 };
    const last = handleInvasiveShot.lastShotInfo;
    const now = Date.now();
    const shotKey = `${x},${y}`;
    let isCollateral = false;
    let collateralType = null; // 'double' or 'multi'
    // If we're in a global collateral window, skip all SFX except doubleKill/multikill
    const inCollateralWindow = now < collateralRef.current.until;
    if (last.pos === shotKey && now - last.time < 30) {
      last.count++;
      if (last.count === 2 || last.count > 2) {
        isCollateral = true;
        // Start a global suppression window for all SFX except double/multi kill
        collateralRef.current.until = now + 200; // 200ms window
        // Show Collateral! effect
        const screenW = window.innerWidth;
        const screenH = window.innerHeight;
        let left, top, transform = '';
        if (x > screenW * 0.75) {
          left = x - 120;
          transform = 'translateY(-100%)';
        } else {
          left = x + 24;
          transform = 'translateY(-100%)';
        }
        if (y < screenH * 0.2) {
          top = y + 32;
          transform = '';
        } else {
          top = y - 24;
        }
        // Determine collateral type
        if (last.count === 2) {
          collateralType = 'double';
        } else if (last.count > 2) {
          collateralType = 'multi';
        }
        setCollateralEffect({
          x: left,
          y: top,
          transform,
          id: Date.now() + Math.random(),
          type: collateralType,
        });
        // Play correct SFX immediately
        if (typeof playSoundFx === 'function') {
          if (collateralType === 'multi') {
            playSoundFx('bookShot');
          } else {
            playSoundFx('doubleKill');
          }
        }
        setTimeout(() => setCollateralEffect(null), 1500);
      }
    } else {
      last.pos = shotKey;
      last.count = 1;
      last.time = now;
    }

    // Track invasive kills ONCE per shot, regardless of how many invasives are hit
    // Only increment on the first invasive hit in this click position
    const isFirstHitInClick = last.count === 1;
    if (isFirstHitInClick) {
      setInvasiveKillCount(prevCount => {
        const newCount = prevCount + 1;
  // ...removed debug log for production...
        if (newCount % 3 === 0) {
          // ...removed debug log for production...
          // Spawn a new prawn
          setPrawnStates(prevPrawns => [
            ...prevPrawns,
            {
              alive: true,
              isFried: Math.floor(Math.random() * 100) === 0
            }
          ]);
        }
        return newCount;
      });
    }

    setInvasiveStates(prev => {
      const newStates = [...prev];
      newStates[i].alive = false;
      return newStates;
    });
    // Play correct SFX for invasive hit, but skip if in collateral window (doubleKill already played)
    if (!isCollateral && !inCollateralWindow) {
      const imgKey = invasivesContext.keys()[invasiveImageIndexes[i]].replace(/^.*\/(.*)$/, '$1');
      const sfxKey = invasiveSfxMap[imgKey];
      if (sfxKey) {
        playSoundFx(sfxKey);
      } else {
        // fallback: random invasive shot
        const idx = Math.floor(Math.random() * 14);
        playSoundFx(`invasiveShots_${idx}`);
      }
    }
    window.bloodEffectActive = true; // Global flag for MovingSprites throttling
    setBloodEffect({
      x, y,
      onComplete: () => {
        // Use batchUpdate to prevent CLS timing collisions with MovingSprites animation loop  
        batchUpdate(() => {
          setBloodEffect(null);
          window.bloodEffectActive = false; // Clear global flag
        });
      }
    });
  };

  // Miss click logic (laser effect only)
  React.useEffect(() => {
    if (modal.isOpen || gameComplete || gameLost) return;
    const handleMissClick = (e) => {
      // Only trigger if click is not on a prawn, invasive, or header/nav element
      const element = document.elementFromPoint(e.clientX, e.clientY);
      if (
        element && (
          element.classList.contains('prawn') ||
          element.classList.contains('invasive') ||
          element.classList.contains('logo') ||
          element.classList.contains('nav-link-left') ||
          element.classList.contains('nav-link-center') ||
          element.classList.contains('mute-button-header') ||
          element.classList.contains('mute-nav-link') ||
          (
            element.classList.contains('nav-link') &&
            !element.classList.contains('nav-link-left') &&
            !element.classList.contains('nav-link-center')
          )
        )
      ) return;
      setLaserEffect({ x: e.clientX, y: e.clientY, hit: false, id: Date.now() + Math.random() });
      setTimeout(() => setLaserEffect(null), 400);
      // Play missed shot SFX
      playSoundFx('laserMiss');
    };
    // Use capture phase to ensure this runs before sprite click handlers
    window.addEventListener('mousedown', handleMissClick, true);
    // Touch support for mobile
    const handleMissTouch = (e) => {
      const touch = e.touches[0];
      if (!touch) return;
      const element = document.elementFromPoint(touch.clientX, touch.clientY);
      if (
        element && (
          element.classList.contains('prawn') ||
          element.classList.contains('invasive') ||
          element.classList.contains('logo') ||
          element.classList.contains('nav-link-left') ||
          element.classList.contains('nav-link-center') ||
          element.classList.contains('mute-button-header') ||
          element.classList.contains('mute-nav-link') ||
          (
            element.classList.contains('nav-link') &&
            !element.classList.contains('nav-link-left') &&
            !element.classList.contains('nav-link-center')
          )
        )
      ) return;
      setLaserEffect({ x: touch.clientX, y: touch.clientY, hit: false, id: Date.now() + Math.random() });
      setTimeout(() => setLaserEffect(null), 400);
      playSoundFx('laserMiss');
    };
    window.addEventListener('touchstart', handleMissTouch, true);
    
    // Additional event listeners to prevent text selection on long press/hold
    const preventSelection = (e) => {
      e.preventDefault();
    };
    
    const preventDragStart = (e) => {
      e.preventDefault();
      return false;
    };
    
    // Prevent selection events
    document.addEventListener('selectstart', preventSelection, false);
    document.addEventListener('dragstart', preventDragStart, false);
    window.addEventListener('dragstart', preventDragStart, false);
    
    return () => {
      window.removeEventListener('mousedown', handleMissClick, true);
      window.removeEventListener('touchstart', handleMissTouch, true);
      document.removeEventListener('selectstart', preventSelection, false);
      document.removeEventListener('dragstart', preventDragStart, false);
      window.removeEventListener('dragstart', preventDragStart, false);
    };
  }, [modal.isOpen, gameComplete, gameLost, playSoundFx]);

  // Render prawns and invasives using MovingSprites
  return (
    <>
      {/* Centered timer display - use flexbox for perfect centering in viewport */}
      {!modal.isOpen && (
        <div style={{
          position: 'fixed',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 5, // lower than sprites
          pointerEvents: 'none',
          boxSizing: 'border-box',
        }}>
          <TimerDisplay time={time} />
        </div>
      )}
      {!modal.isOpen && (
        <>
          {/* Invasives (targets) */}
          {(() => {
            // Precompute per-sprite sizes for all invasives
            // Map PNG index to special size logic if needed
            const getSpecialSize = (img, fallback, key) => {
              // Slightly smaller at upper end for sixpackring.png
              if (key && key.includes('sixpackring.png')) return Math.floor(32 + Math.random() * (54 - 32));
              // Bigger at upper end
              if (key && key.includes('anglerfish.png')) return Math.floor(60 + Math.random() * (80 - 60));
              if (key && key.includes('blobfish3.png')) return Math.floor(60 + Math.random() * (80 - 60));
              if (key && key.includes('plasticbag.png')) return Math.floor(60 + Math.random() * (80 - 60));
              if (key && key.includes('mitchface.png')) return Math.floor(70 + Math.random() * (80 - 70));
              if (key && key.includes('markface.png')) return Math.floor(60 + Math.random() * (82 - 70));
              // Slightly bigger at both ends for straw and energydrink
              if (key && key.includes('straw.png')) return Math.floor(46 + Math.random() * (52 - 46));
              if (key && key.includes('energydrink.png')) return Math.floor(46 + Math.random() * (52 - 46));
              // Smaller at upper end
              if (key && key.includes('vape.png')) return Math.floor(32 + Math.random() * (44 - 32));
              if (key && key.includes('wrapper.png')) return Math.floor(32 + Math.random() * (44 - 32));
              // Existing special cases
              if (img === monkfishImage) return Math.floor(75 + Math.random() * (94 - 65));
              if (img === smilingsalamanderImage) return Math.floor(55 + Math.random() * (78 - 48));
              if (img === blobfishImage) return Math.floor(40 + Math.random() * (52 - 40));
              if (img === deepseacreatureImage) return Math.floor(32 + Math.random() * (40 - 32));
              return fallback;
            };
            const spriteSizes = invasiveImageIndexes.map(i =>
              getSpecialSize(allInvasiveImages[i], Math.floor(INVASIVE_SIZE_RANGE[0] + Math.random() * (INVASIVE_SIZE_RANGE[1] - INVASIVE_SIZE_RANGE[0])), invasivesContext.keys()[i])
            );
            return (
              <MovingSprites
                count={invasiveImageIndexesArr.length}
                images={invasiveImageIndexes.map(i => allInvasiveImages[i])}
                sizeRange={INVASIVE_SIZE_RANGE}
                spriteSizes={spriteSizes}
                speedRange={INVASIVE_SPEED_RANGE}
                zIndex={10}
                spriteState={invasiveStates}
                setSpriteState={setInvasiveStates}
                onShot={(i, x, y, event) => handleInvasiveShot(i, x, y, event)}
                className="invasive"
                disabled={gameComplete || gameLost}
                facing={invasiveImageIndexes.map(i => {
                  const img = allInvasiveImages[i];
                  const key = invasivesContext.keys()[i];
                  if (key.includes('anglerfish.png')) return 'right';
                  if (key.includes('blobfish2.png')) return 'left';
                  if (key.includes('blobfish3.png')) return 'right';
                  if (key.includes('gascan.png')) return 'right';
                  if (key.includes('mitchface.png')) return 'none';
                  if (key.includes('markface.png')) return 'none';
                  if (img === monkfishImage || img === smilingsalamanderImage) return 'right';
                  return 'none';
                })}
                extraStylePerSprite={idx => {
                  const img = allInvasiveImages[invasiveImageIndexes[idx]];
                  if (img === monkfishImage || img === smilingsalamanderImage) {
                    return { filter: 'drop-shadow(0 0 8px #baffff) drop-shadow(0 0 4px #fff)' };
                  }
                  return {};
                }}
                blobfishImage={blobfishImage}
                deepseacreatureImage={deepseacreatureImage}
              />
            );
          })()}
          {/* Prawns (obstacles) */}
          <MovingSprites
            count={prawnStates.length}
            images={isHdPrawnMode ? prawn8kImage : prawnImage}
            sizeRange={PRAWN_SIZE_RANGE}
            speedRange={PRAWN_SPEED_RANGE}
            zIndex={20}
            spriteState={prawnStates}
            setSpriteState={setPrawnStates}
            onShot={handlePrawnShot}
            className={isHdPrawnMode ? 'prawn hd-prawn' : 'prawn'}
            disabled={gameComplete || gameLost}
            facing="left"
            enableSpawnAnimation={true}
          />
        </>
      )}
      {/* Render laser effect for hits and misses */}
      {laserEffect && laserEffect.hit && (
        <div
          className="laser-flash"
          style={{
            position: 'fixed',
            left: laserEffect.x - 1,
            top: laserEffect.y - 1,
            width: 2,
            height: 2,
            pointerEvents: 'none',
            zIndex: 9999,
            borderRadius: '50%',
            boxShadow: '0 0 2px 1px #afb3eb, 0 0 0.5px 0.25px #afb3eb',
            background: 'radial-gradient(circle, #afb3eb 60%, transparent 100%)',
            opacity: 0.85,
            animation: 'laser-flash-hit 0.18s linear',
            willChange: 'opacity, transform',
            contain: 'layout style paint',
            backfaceVisibility: 'hidden',
          }}
        />
      )}
      {laserEffect && !laserEffect.hit && (
        <div
          className="laser-flash-miss"
          style={{
            position: 'fixed',
            left: laserEffect.x - 1,
            top: laserEffect.y - 1,
            width: 2,
            height: 2,
            pointerEvents: 'none',
            zIndex: 9999,
            borderRadius: '50%',
            boxShadow: '0 0 2px 1px #afb3eb, 0 0 0.5px 0.25px #afb3eb',
            background: 'radial-gradient(circle, #afb3eb 60%, transparent 100%)',
            opacity: 0.85,
            animation: 'laser-flash-miss 0.4s linear',
            willChange: 'opacity, transform',
            contain: 'layout style paint',
            backfaceVisibility: 'hidden',
          }}
        />
      )}
      {/* Render blood effect - Always mount to prevent CLS */}
      <UnderwaterBloodEffect
        x={bloodEffect?.x || 0}
        y={bloodEffect?.y || 0}
        onComplete={bloodEffect?.onComplete || (() => {})}
        isActive={!!bloodEffect}
      />
      {/* Collateral effect */}
      {collateralEffect && (
        <div
          key={collateralEffect.id}
          style={{
            position: 'fixed',
            left: collateralEffect.x,
            top: collateralEffect.y,
            zIndex: 99999,
            color: '#222',
            fontWeight: 700,
            fontSize: '1.1rem',
            textShadow: '0 1px 8px #fff, 0 0 2px #fff',
            pointerEvents: 'none',
            opacity: 1,
            animation: 'collateral-fade 1.5s linear',
            transition: 'opacity 0.3s',
            userSelect: 'none',
            transform: collateralEffect.transform,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            lineHeight: 1.1,
          }}
        >
          {collateralEffect.type === 'multi' ? (
            <>
              <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>Multi</span>
              <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>Kill</span>
            </>
          ) : (
            <>
              <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>Double</span>
              <span style={{ display: 'block', width: '100%', textAlign: 'center' }}>Kill</span>
            </>
          )}
        </div>
      )}
      {/* Copyright notice */}
      <div 
        className="copyright-notice"
        style={{
          position: 'fixed',
          bottom: '10px',
          left: '6vw',
          transform: 'translateX(-50%)',
          fontSize: '12px',
          fontWeight: '500',
          color: '#fff',
          textShadow: '0px 0px 2px #000, 0px 0px 3px #000',
          zIndex: 30,
          userSelect: 'none',
          opacity: 0.8,
          pointerEvents: 'none',
        }}
      >
        © {new Date().getFullYear()} Labby Labs LLC
      </div>

      <style>{`
        @keyframes laser-flash-hit {
          0% { opacity: 0.85; transform: scale(1); }
          60% { opacity: 1; transform: scale(1.2); }
          100% { opacity: 0; transform: scale(1.4); }
        }
        @keyframes laser-flash-miss {
          0% { opacity: 0.85; transform: scale(1); }
          60% { opacity: 0.5; transform: scale(0.7); }
          100% { opacity: 0; transform: scale(0.1); }
        }
        @keyframes collateral-fade {
          0% { opacity: 1; }
          80% { opacity: 1; }
          100% { opacity: 0; }
        }
        .hd-prawn {
        }
        .simulator {
          user-select: none;
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          touch-action: manipulation;
          -webkit-touch-callout: none;
          -webkit-tap-highlight-color: transparent;
        }
        @media (max-width: 768px) {
          .copyright-notice {
            left: 20vw !important;
          }
        }
      `}</style>
    </>
  );
};

export default KillSimulator;
